import { useRef, useEffect, useState } from "react";
import { Camera, Paperclip, Send, FileText, Lock } from "lucide-react";
import { groupMessagesByDay } from "@/lib/format";

const Bubble = ({ item }) => {
  // Côté client : "mine" = client (from === "client")
  const mine = item.from === "client";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
        {item.attachment?.type === "image" && (
          <div
            className={[
              "mb-1.5 overflow-hidden",
              mine ? "rounded-3xl rounded-br-lg" : "rounded-3xl rounded-bl-lg",
            ].join(" ")}
          >
            <img src={item.attachment.url} alt="" className="max-h-56 w-auto object-cover" />
          </div>
        )}
        {item.attachment?.type === "pdf" && (
          <div
            className={[
              "mb-1.5 flex items-center gap-2.5 px-3 py-2.5",
              mine
                ? "bg-white border border-[#EEF2F5] rounded-2xl rounded-br-md"
                : "bg-white border border-[#EEF2F5] rounded-2xl rounded-bl-md",
            ].join(" ")}
          >
            <div className="h-9 w-9 rounded-lg bg-[#FEE2E2] flex items-center justify-center text-[#991B1B]">
              <FileText size={16} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="text-[12.5px] font-semibold text-[#111111] truncate">
                {item.attachment.name}
              </p>
              <p className="text-[10.5px] text-[#9CA3AF]">
                PDF · {item.attachment.size || ""}
              </p>
            </div>
          </div>
        )}
        {item.text && (
          <div
            className={[
              "px-4 py-2.5 text-[14.5px] leading-snug",
              mine
                ? "bg-[#111111] text-white rounded-[22px] rounded-br-lg"
                : "bg-[#F1F5F9] text-[#111111] rounded-[22px] rounded-bl-lg",
            ].join(" ")}
          >
            {item.text}
          </div>
        )}
        <span className="mt-1 text-[10.5px] text-[#9CA3AF] px-1">
          {item.time}
        </span>
      </div>
    </div>
  );
};

const ClientMessages = ({ messages = [], advisor = null, readOnly = false, onSend }) => {
  const scrollerRef = useRef(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const groups = groupMessagesByDay(messages);

  useEffect(() => {
    scrollerRef.current?.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  const send = async () => {
    if (!text.trim() || sending || readOnly) return;
    setSending(true);
    try {
      await onSend?.(text);
      setText("");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      data-testid="client-messages"
      className="wyvio-fade-in flex flex-col h-[calc(100vh-56px-96px)]"
    >
      <div className="mb-2">
        <h2 className="text-[26px] font-semibold tracking-tight text-[#111111]">
          Messages
        </h2>
        <p className="mt-1.5 text-[13px] text-[#6B7280] leading-relaxed">
          {readOnly
            ? "La messagerie est archivée depuis la livraison de votre véhicule."
            : `Discutez directement avec ${advisor?.name || "votre commercial"}.`}
        </p>
      </div>

      <div
        ref={scrollerRef}
        className="flex-1 -mx-5 px-5 overflow-y-auto space-y-4 py-4"
      >
        {groups.length === 0 && (
          <p className="text-center text-[12.5px] text-[#9CA3AF] py-10">
            Aucun message pour le moment.
          </p>
        )}
        {groups.map((group) => (
          <div key={group.id}>
            <div className="flex items-center justify-center mb-3">
              <span className="text-[10.5px] font-medium text-[#9CA3AF] bg-white/80 px-2.5 py-0.5 rounded-full">
                {group.day}
              </span>
            </div>
            <div className="space-y-2.5">
              {group.items.map((it) => (
                <Bubble key={it.id} item={it} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 pt-2 pb-2 bg-gradient-to-t from-[#F7F8FA] via-[#F7F8FA] to-transparent">
        {readOnly ? (
          <div className="h-12 rounded-full border border-[#EEF2F5] bg-white/70 px-4 flex items-center justify-center gap-2 text-[12.5px] font-medium text-[#6B7280]">
            <Lock size={12} strokeWidth={2} />
            Messagerie fermée — dossier terminé
          </div>
        ) : (
          <div className="flex items-center gap-2 h-12 rounded-full border border-[#EEF2F5] bg-white px-3 shadow-[0_1px_2px_rgba(17,17,17,0.03)]">
            <button
              data-testid="client-msg-photo"
              className="h-9 w-9 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center text-[#6B7280]"
              aria-label="Photo"
            >
              <Camera size={16} strokeWidth={2} />
            </button>
            <button
              data-testid="client-msg-attach"
              className="h-9 w-9 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center text-[#6B7280]"
              aria-label="Pièce jointe"
            >
              <Paperclip size={15} strokeWidth={2} />
            </button>
            <input
              data-testid="client-msg-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Votre message…"
              className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-[#9CA3AF] text-[#111111]"
            />
            <button
              data-testid="client-msg-send"
              onClick={send}
              disabled={sending || !text.trim()}
              className="h-9 w-9 rounded-full bg-[#111111] text-white flex items-center justify-center hover:bg-black transition-colors disabled:opacity-40"
              aria-label="Envoyer"
            >
              <Send size={14} strokeWidth={2.25} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ClientMessages;
